import {Component, ElementRef, EventEmitter, HostBinding, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild} from '@angular/core';
import {finalize, Subscription, timer} from "rxjs";

import {Saseung3} from '../../shared/models/saseung3.model';
import {PRELOAD_RATIO} from 'src/app/shared/defines/desktop.const';
import {IsHangul} from 'src/app/shared/defines/gukeo.func';
import {ListLoader} from "../../shared/interfaces/list-loader";
import {BookmarkChange, SearchParam, UI_RELOAD_HEIGHT} from "../../shared/defines/client.const";
import {Saseung3HanjaService} from "../../shared/services/saseung3-hanja/saseung3-hanja.service";
import {Saseung3BookmarkService} from "../../shared/services/saseung3-hanja/saseung3-bookmark.service";
import {RedPipeService} from "../../shared/services/red-pipe/red-pipe.service";


@Component({
  selector: 'app-saseung3-detail',
  templateUrl: './saseung3-detail.component.html',
  styleUrls: ['./saseung3-detail.component.scss']
})
export class Saseung3DetailComponent implements OnDestroy, OnChanges {
  @HostBinding('attr.class') cssClass = 'schedule-list-box';
  @HostBinding('style.display') styleBlock = 'block';
  @ViewChild('scrollContainer') scrollContainer?: ElementRef;
  @ViewChild('reload') reload?: ElementRef;

  @Input() hangul: string | null = null;
  @Input() keyword: string | null = null;
  @Input() hanja: string | null = null;
  @Input() bookmark = false;
  @Output() bookmarkChange = new EventEmitter<BookmarkChange>();

  protected get dispTxt(): string | null {
    if (this.bookmark) {
      return null;
    }

    // keyword 검색만 있는 경우 list-tit-box를 표시하지 않음
    // hanja, hangul이 있는 경우에만 표시
    if (this.keyword && !this.hanja && !this.hangul) {
      return null;
    }
    return this.hanja ?? this.hangul ?? null;
  }

  // hangul 표시 여부: dispTxt가 1글자 한글인 경우만 숨김
  protected get showHangul(): boolean {
    return this.bookmark || !IsHangul(this.dispTxt);
  }

  // 화면 크기 조정
  private bodyHeight = UI_RELOAD_HEIGHT;

  // redPipe 처리
  private redPipe$: Subscription | null = null;

  protected isNoResult(): boolean {
    return (this.ll?.IsComplete() ?? false) && (this.ll?.getList()?.length == 0 ?? false);
  }

  // ListLoader 설정
  ll: ListLoader<Saseung3> | null = null;
  private getNextList$: Subscription | null = null;
  private needReload = false;

  constructor(
    private saseung3HanjaSvc: Saseung3HanjaService,
    private saseung3BookmarkSvc: Saseung3BookmarkService,
    private redPipeSvc: RedPipeService,
  ) {
    this.redPipe$ = this.redPipeSvc.Click$.subscribe((red) => {
      this.scrollContainer?.nativeElement.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    });
  }

  ngOnDestroy(): void {
    this.redPipe$?.unsubscribe();
    this.redPipe$ = null;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('bookmark' in changes && changes['bookmark'].currentValue) {
      this.hangul = null;
      this.keyword = null;
      this.hanja = null;

      this.ll = this.saseung3BookmarkSvc;

      this.ReloadList({});
    }
    else if ('hanja' in changes && changes['hanja'].currentValue) {
      this.hangul = null;
      this.keyword = null;

      this.ll = this.saseung3HanjaSvc;

      const param = {hanja: changes['hanja'].currentValue};
      this.ReloadList(param);
    }
    else if ('keyword' in changes && changes['keyword'].currentValue) {
      // keyword가 있으면 다른 파라미터 무시.
      this.hangul = null;
      this.hanja = null;

      this.ll = this.saseung3HanjaSvc;

      const param = {keyword: changes['keyword'].currentValue};
      this.ReloadList(param);
    }
    else if ('hangul' in changes && changes['hangul'].currentValue) {
      this.keyword = null;
      this.hanja = null;

      this.ll = this.saseung3HanjaSvc;

      const param = {hangul: changes['hangul'].currentValue};
      this.ReloadList(param);
    }
  }

  protected onBookmarkChange(bookmarkChange: BookmarkChange): void {
    if (this.bookmark && !bookmarkChange.bookmark) {
      this.saseung3BookmarkSvc.hideBookmark(String(bookmarkChange.id));
    }
    this.bookmarkChange.emit(bookmarkChange);
  }

  private ReloadList(param: SearchParam): void {
    this.ll?.ResetList(param);
    this.loadList();
  }

  private loadList(): void {
    if (this.getNextList$) {
      console.warn('Saseung3DetailComponent::loadList() - already call loadList()');
      return;
    }

    if (this.ll?.IsComplete()) {
      console.warn('Saseung3DetailComponent::loadList() - already load all Saseung3');
      return;
    }

    if (this.ll) {
      this.getNextList$ = this.ll?.GetNextList().pipe(
        finalize(() => {
          this.getNextList$ = null;

          if (this.needReload) {
            timer(0).subscribe(() => {
              this.needReload = false;
              this.loadList();
            });
          }
        }),
      ).subscribe();
    }
  }

  /****************************************************************************
   * 일정 범위 이상 스크롤되면 남은 단어를 자동 로딩한다.
   ****************************************************************************/
  onScroll($event: Event): void {
    if (this.scrollContainer === undefined) {
      throw new Error('Saseung3DetailComponent::onScroll() - #scrollContainer  does not exist!!');
    }

    if (this.ll && !this.ll.IsLoading() && !this.ll.IsComplete() && this.reload) {
      const tar = $event.target || $event.srcElement || $event.currentTarget;
      const elContainer = this.scrollContainer.nativeElement;
      if (tar !== elContainer) {
        console.error('Saseung3DetailComponent::onScroll() - wrong structure!!');
      }

      const elReload = this.reload.nativeElement;

      if (elReload.offsetTop < elContainer.scrollTop + PRELOAD_RATIO * this.bodyHeight) {
        this.loadList();
      } else {
        // 스크롤 중에 이미 로딩이 진행되고 있으면, 로딩이 끝난 뒤에 다시 로딩이 되도록 요청. (비동기처리 순서 꼬임 방지)
        this.needReload = true;
      }
    }
  }
}
